import React, { useState, useEffect } from 'react';
import { Star, MapPin, Clock, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { getRestaurantInfo } from '../services/api';

// Default data jika API gagal
const DEFAULT_INFO = {
  name: 'Rumah Makan Aroma Cempaka',
  tagline: 'Cita Rasa Autentik Masakan Padang',
  rating: 4.4,
  totalReviews: 2444,
  priceRange: 'Rp 25.000 – Rp 75.000',
  openHours: 'Buka sampai pukul 21.00',
  services: ['Dine-in', 'Takeaway', 'No-contact delivery', 'Order online'],
  heroImage: 'https://images.unsplash.com/photo-1677921755291-c39158477b8e'
};

const HeroSection = () => {
  const [info, setInfo] = useState(DEFAULT_INFO);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getRestaurantInfo();
      if (data) {
        setInfo({ ...DEFAULT_INFO, ...data });
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const rating = info.rating || 0;
  const totalReviews = info.totalReviews || 0;
  const services = Array.isArray(info.services) ? info.services : [];

  return (
    <section
      id="beranda"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={info.heroImage || DEFAULT_INFO.heroImage}
          alt="Masakan Padang"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
      </div>

      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-600/20 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(rating)
                      ? 'text-amber-400 fill-amber-400'
                      : 'text-gray-400'
                  }`}
                />
              ))}
            </div>
            <span className="text-white font-medium">
              {rating} ({totalReviews.toLocaleString()} ulasan)
            </span>
          </div>

          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {info.name}
          </h1>

          <p className="text-xl md:text-2xl text-white/90 mb-4 font-light">
            {info.tagline}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            <div className="flex items-center gap-2 text-white/80">
              <MapPin className="w-5 h-5 text-amber-400" />
              <span>Kota Jambi</span>
            </div>
            <div className="w-1.5 h-1.5 bg-white/40 rounded-full hidden sm:block" />
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-5 h-5 text-amber-400" />
              <span>{info.openHours}</span>
            </div>
            <div className="w-1.5 h-1.5 bg-white/40 rounded-full hidden sm:block" />
            <div className="text-amber-400 font-semibold">
              {info.priceRange}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => scrollToSection('#menu')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              Lihat Menu
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('#kontak')}
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg transition-all duration-300 hover:-translate-y-1 bg-white/10 backdrop-blur-sm"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Petunjuk Arah
            </Button>
          </div>

          <div className="mt-16 flex flex-wrap items-center justify-center gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-white/70 text-sm"
              >
                <div className="w-2 h-2 bg-green-400 rounded-full" />
                {service}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/40 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
